// src/routes/index.tsx
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { motion } from 'framer-motion';
import { assetStore } from '../stores/assetStore';
import { riskStore } from '../stores/riskStore';
import { nonInfoSecRiskStore, getNonInfoSecRiskLevel } from '../stores/nonInfoSecRiskStore';
import { soaStore } from '../stores/soaStore';
import { riskMatrixStore } from '../stores/riskMatrixStore';
import { iso27001Controls } from '../data/iso27001Controls';
import { calculateExpiry, formatDate } from '../utils/dateUtils';
import { NavigationSidebar } from '../components/layout/NavigationSidebar';

export const Route = createFileRoute('/')({
  component: Dashboard,
});

// Helper to calculate risk level from matrix
function calculateRiskLevel(likelihood: string, severity: string, matrixType: 'infoSec' | 'nonInfoSec'): 'green' | 'amber' | 'red' {
  const likelihoodLabels = matrixType === 'infoSec' 
    ? ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain']
    : ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
  const severityLabels = matrixType === 'infoSec'
    ? ['Insignificant', 'Minor', 'Moderate', 'Serious', 'Catastrophic']
    : ['Insignificant', 'Minor', 'Moderate', 'Serious', 'Catastrophic'];
  
  const likelihoodIdx = likelihoodLabels.indexOf(likelihood);
  const severityIdx = severityLabels.indexOf(severity);
  
  if (likelihoodIdx >= 0 && severityIdx >= 0) {
    try {
      const colors = matrixType === 'infoSec' 
        ? riskMatrixStore.infoSec.colors 
        : riskMatrixStore.nonInfoSec.colors;
      if (colors && colors[likelihoodIdx]) {
        return colors[likelihoodIdx][severityIdx] || 'green';
      }
    } catch (e) {
      // Fallback
    }
  }
  return 'green';
}

// Mini donut chart component
function DonutChart({ 
  data, 
  colors, 
  size = 120,
  title
}: { 
  data: number[]; 
  colors: string[];
  size?: number;
  title: string;
}) {
  const total = data.reduce((a, b) => a + b, 0);
  if (total === 0) {
    return (
      <div className="flex flex-col items-center">
        <div 
          className="rounded-full bg-gray-200 flex items-center justify-center"
          style={{ width: size, height: size }}
        >
          <span className="text-gray-400 text-sm">No data</span>
        </div>
        <p className="text-xs text-gray-500 mt-2">{title}</p>
      </div>
    );
  }
  
  let currentAngle = -90;
  const radius = size / 2 - 10;
  const center = size / 2;
  
  const segments = data.map((value, idx) => {
    const percentage = (value / total) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);
    
    const largeArc = angle > 180 ? 1 : 0;
    
    const pathD = `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    
    return (
      <path
        key={idx}
        d={pathD}
        fill={colors[idx]}
        className="transition-opacity hover:opacity-80"
      />
    );
  });
  
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-0">
        {segments}
        <circle cx={center} cy={center} r={radius * 0.6} fill="white" />
        <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" className="text-lg font-bold fill-gray-700">
          {total}
        </text>
      </svg>
      <p className="text-xs text-gray-500 mt-2">{title}</p>
    </div>
  );
}

// Horizontal bar chart component
function HorizontalBarChart({ 
  data, 
  maxValue 
}: { 
  data: { label: string; value: number; color: string }[];
  maxValue?: number;
}) {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="space-y-2">
      {data.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="text-xs text-gray-600 w-24 truncate" title={item.label}>{item.label}</span>
          <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(item.value / max) * 100}%` }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="h-full rounded-full"
              style={{ backgroundColor: item.color }}
            />
          </div>
          <span className="text-xs font-medium text-gray-700 w-8 text-right">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

// Stat card component
function StatCard({ 
  title, 
  value, 
  subtitle, 
  color = 'gray',
  icon
}: { 
  title: string; 
  value: number | string; 
  subtitle?: string;
  color?: string;
  icon?: React.ReactNode;
}) {
  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-50 border-gray-200',
    green: 'bg-green-50 border-green-200',
    amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
    blue: 'bg-blue-50 border-blue-200',
    purple: 'bg-purple-50 border-purple-200',
  };
  
  const textColorClasses: Record<string, string> = {
    gray: 'text-gray-700',
    green: 'text-green-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]} transition-all hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        {icon}
      </div>
      <p className={`text-2xl font-bold mt-1 ${textColorClasses[color]}`}>{value}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

// Section header component
function SectionHeader({ title, icon, color }: { title: string; icon: React.ReactNode; color: string }) {
  return (
    <div className={`flex items-center gap-3 mb-4 pb-2 border-b-2 ${color}`}>
      <div className="p-2 rounded-lg bg-white shadow-sm">{icon}</div>
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
    </div>
  );
}

function Dashboard() {
  const assetSnap = useSnapshot(assetStore);
  const riskSnap = useSnapshot(riskStore);
  const nonInfoSecSnap = useSnapshot(nonInfoSecRiskStore);
  const soaSnap = useSnapshot(soaStore);
  
  // ========== ASSET METRICS ==========
  const assetMetrics = useMemo(() => {
    const assets = assetSnap.assets || [];
    const total = assets.length;
    const infoSecAssets = assets.filter(a => a.isISO27001).length;
    const nonInfoSecAssets = total - infoSecAssets;
    
    // By asset group
    const byCategory: Record<string, number> = {};
    assets.forEach(a => {
      const cat = a.assetGroup || 'Ungrouped';
      byCategory[cat] = (byCategory[cat] || 0) + 1;
    });
    
    // By location
    const byLocation: Record<string, number> = {};
    assets.forEach(a => {
      const loc = a.location || 'Unassigned';
      byLocation[loc] = (byLocation[loc] || 0) + 1;
    });
    
    // Compliance status (due within 30 days or overdue)
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    let overdueItems = 0;
    let upcomingItems = 0;

    assets.forEach(a => {
      const expiries = [
        calculateExpiry(a.auditDate, a.auditPeriodicity),
        calculateExpiry(a.calibrationDate, a.calibrationPeriodicity),
        calculateExpiry(a.certificationDate, a.certificationPeriodicity),
        calculateExpiry(a.testDate, a.testPeriodicity),
      ].filter((d): d is Date => d !== null);

      if (expiries.some(d => d < today)) overdueItems++;
      else if (expiries.some(d => d <= thirtyDaysFromNow)) upcomingItems++;
    });
    
    return {
      total,
      infoSecAssets,
      nonInfoSecAssets,
      byCategory: Object.entries(byCategory)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label, value, color: '#6366f1' })),
      byLocation: Object.entries(byLocation)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label, value, color: '#8b5cf6' })),
      overdueItems,
      upcomingItems
    };
  }, [assetSnap.assets]);
  
  // ========== INFOSEC RISK METRICS ==========
  const infoSecRiskMetrics = useMemo(() => {
    const risks = riskSnap.risks || [];
    const total = risks.length;
    
    // By managed risk level (using matrix colors)
    let high = 0, medium = 0, low = 0;
    risks.forEach(r => {
      const color = calculateRiskLevel(r.managedLikelihood, r.managedSeverity, 'infoSec');
      if (color === 'red') high++;
      else if (color === 'amber') medium++;
      else low++;
    });
    
    // By control status
    const byStatus: Record<string, number> = {};
    risks.forEach(r => {
      const status = r.controlStatus || 'Not Started';
      byStatus[status] = (byStatus[status] || 0) + 1;
    });
    
    // By treatment
    const byTreatment: Record<string, number> = {};
    risks.forEach(r => {
      const treatment = r.treatment || 'Unassigned';
      byTreatment[treatment] = (byTreatment[treatment] || 0) + 1;
    });
    
    // By asset category
    const byCategory: Record<string, number> = {};
    risks.forEach(r => {
      const cat = r.assetCategory || 'Uncategorised';
      byCategory[cat] = (byCategory[cat] || 0) + 1;
    });
    
    return {
      total,
      high,
      medium,
      low,
      byStatus: Object.entries(byStatus)
        .map(([label, value]) => ({ 
          label, 
          value, 
          color: label === 'Implemented' ? '#10b981' : label === 'In Progress' ? '#f59e0b' : '#6b7280' 
        })),
      byTreatment: Object.entries(byTreatment)
        .map(([label, value]) => ({ label, value, color: '#ef4444' })),
      byCategory: Object.entries(byCategory)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label, value, color: '#f43f5e' })),
    };
  }, [riskSnap.risks]);
  
  // ========== NON-INFOSEC RISK METRICS ==========
  const nonInfoSecRiskMetrics = useMemo(() => {
    const risks = nonInfoSecSnap.risks || [];
    const total = risks.length;
    
    // By managed risk level (using matrix colors)
    let high = 0, medium = 0, low = 0;
    risks.forEach(r => {
      const color = calculateRiskLevel(r.managedLikelihood, r.managedSeverity, 'nonInfoSec');
      if (color === 'red') high++;
      else if (color === 'amber') medium++;
      else low++;
    });
    
    // By control status
    const byStatus: Record<string, number> = {};
    risks.forEach(r => {
      const status = r.controlStatus || 'Not Started';
      byStatus[status] = (byStatus[status] || 0) + 1;
    });
    
    // By category
    const byCategory: Record<string, number> = {};
    risks.forEach(r => {
      const cats = r.riskCategory.length > 0 ? r.riskCategory : ['Uncategorised'];
      cats.forEach(cat => {
        byCategory[cat] = (byCategory[cat] || 0) + 1;
      });
    });
    
    // By treatment
    const byTreatment: Record<string, number> = {};
    risks.forEach(r => {
      const treatment = r.treatment || 'Unassigned';
      byTreatment[treatment] = (byTreatment[treatment] || 0) + 1;
    });
    
    return {
      total,
      high,
      medium,
      low,
      byStatus: Object.entries(byStatus)
        .map(([label, value]) => ({ 
          label, 
          value, 
          color: label === 'Implemented' ? '#10b981' : label === 'In Progress' ? '#f59e0b' : '#6b7280' 
        })),
      byCategory: Object.entries(byCategory)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label, value, color: '#f97316' })),
      byTreatment: Object.entries(byTreatment)
        .map(([label, value]) => ({ label, value, color: '#eab308' })),
    };
  }, [nonInfoSecSnap.risks]);
  
  // ========== SOA METRICS ==========
  const soaMetrics = useMemo(() => {
    const totalControls = iso27001Controls.length;
    const justifications = soaSnap.justifications || {};
    const risks = riskSnap.risks || [];
    
    // Helper to get risks for a control (same logic as SoA page)
    const getRisksForControl = (controlName: string) => {
      return risks.filter(r => r.control === controlName);
    };
    
    // Used = controls that have risks linked to them
    // Not Used = controls with no risks linked
    let used = 0;
    let notUsed = 0;
    let hasJustification = 0;
    
    // By theme
    const byTheme: Record<string, { used: number; notUsed: number; total: number }> = {
      'Organisational': { used: 0, notUsed: 0, total: 0 },
      'People': { used: 0, notUsed: 0, total: 0 },
      'Physical': { used: 0, notUsed: 0, total: 0 },
      'Technological': { used: 0, notUsed: 0, total: 0 },
    };
    
    // Implementation status counters
    let implemented = 0;
    let inProgress = 0;
    let notStarted = 0;
    
    iso27001Controls.forEach(control => {
      // Skip custom control
      if (control.theme?.toLowerCase().includes('custom')) return;
      
      // Determine theme
      let themeKey = 'Organisational';
      if (control.theme?.toLowerCase().includes('people')) themeKey = 'People';
      else if (control.theme?.toLowerCase().includes('physical')) themeKey = 'Physical';
      else if (control.theme?.toLowerCase().includes('technological')) themeKey = 'Technological';
      
      if (byTheme[themeKey]) {
        byTheme[themeKey].total++;
      }
      
      // Check if control has linked risks
      const controlRisks = getRisksForControl(control.control);
      const isUsed = controlRisks.length > 0;
      
      if (isUsed) {
        used++;
        if (byTheme[themeKey]) byTheme[themeKey].used++;
        
        // Check implementation status from linked risks
        controlRisks.forEach(risk => {
          if (risk.controlStatus === 'Implemented') implemented++;
          else if (risk.controlStatus === 'In Progress') inProgress++;
          else notStarted++;
        });
      } else {
        notUsed++;
        if (byTheme[themeKey]) byTheme[themeKey].notUsed++;
      }
      
      // Check if has justification
      if (justifications[control.control]) {
        hasJustification++;
      }
    });
    
    // Actual total (excluding custom)
    const actualTotal = used + notUsed;
    
    return {
      totalControls: actualTotal,
      used,
      notUsed,
      hasJustification,
      implemented,
      inProgress,
      notStarted,
      byTheme,
      completionPercentage: actualTotal > 0 ? Math.round((used / actualTotal) * 100) : 0,
    };
  }, [soaSnap.justifications, riskSnap.risks]);
  
  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      <NavigationSidebar />
      <div className="flex-1 overflow-auto min-w-0">
        {/* Header */}
        <div 
          className="sticky top-0 z-10 px-6 py-4 text-white shadow-lg"
          style={{ background: 'var(--color-accent)' }}
        >
          <h1 className="text-xl font-bold">Dashboard</h1>
          <p className="text-sm text-purple-200">Overview of your compliance management system</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* ========== ASSETS SECTION ========== */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
            >
              <SectionHeader 
                title="Assets" 
                icon={<svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>}
                color="border-indigo-500"
              />
              
              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <StatCard title="Total" value={assetMetrics.total} color="blue" />
                <StatCard title="Info Sec" value={assetMetrics.infoSecAssets} color="purple" />
                <StatCard title="Non-Info Sec" value={assetMetrics.nonInfoSecAssets} color="gray" />
              </div>
              
              {/* Charts */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">By Asset Group</p>
                  <HorizontalBarChart data={assetMetrics.byCategory} />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">By Location</p>
                  <HorizontalBarChart data={assetMetrics.byLocation} />
                </div>
              </div>
              
              {/* Audit alerts */}
              {(assetMetrics.overdueItems > 0 || assetMetrics.upcomingItems > 0) && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex gap-4">
                    {assetMetrics.overdueItems > 0 && (
                      <div className="flex items-center gap-2 text-red-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                        <span className="text-sm font-medium">{assetMetrics.overdueItems} overdue items</span>
                      </div>
                    )}
                    {assetMetrics.upcomingItems > 0 && (
                      <div className="flex items-center gap-2 text-amber-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
                        <span className="text-sm font-medium">{assetMetrics.upcomingItems} due within 30 days</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
            
            {/* ========== INFOSEC RISKS SECTION ========== */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
            >
              <SectionHeader 
                title="Information Security Risks" 
                icon={<svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
                color="border-red-500"
              />
              
              {/* Stats row */}
              <div className="grid grid-cols-4 gap-3 mb-5">
                <StatCard title="Total" value={infoSecRiskMetrics.total} color="gray" />
                <StatCard title="High" value={infoSecRiskMetrics.high} color="red" />
                <StatCard title="Medium" value={infoSecRiskMetrics.medium} color="amber" />
                <StatCard title="Low" value={infoSecRiskMetrics.low} color="green" />
              </div>
              
              {/* Charts */}
              <div className="flex gap-6 items-start">
                <DonutChart 
                  data={[infoSecRiskMetrics.high, infoSecRiskMetrics.medium, infoSecRiskMetrics.low]}
                  colors={['#ef4444', '#f59e0b', '#10b981']}
                  title="Risk Distribution"
                />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Control Status</p>
                  <HorizontalBarChart data={infoSecRiskMetrics.byStatus} />
                </div>
              </div>
              
              {/* Top categories */}
              {infoSecRiskMetrics.byCategory.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Top Asset Categories</p>
                  <HorizontalBarChart data={infoSecRiskMetrics.byCategory} />
                </div>
              )}
            </motion.div>
            
            {/* ========== NON-INFOSEC RISKS SECTION ========== */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
            >
              <SectionHeader 
                title="Business Risks" 
                icon={<svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                color="border-amber-500"
              />
              
              {/* Stats row */}
              <div className="grid grid-cols-4 gap-3 mb-5">
                <StatCard title="Total" value={nonInfoSecRiskMetrics.total} color="gray" />
                <StatCard title="High" value={nonInfoSecRiskMetrics.high} color="red" />
                <StatCard title="Medium" value={nonInfoSecRiskMetrics.medium} color="amber" />
                <StatCard title="Low" value={nonInfoSecRiskMetrics.low} color="green" />
              </div>
              
              {/* Charts */}
              <div className="flex gap-6 items-start">
                <DonutChart 
                  data={[nonInfoSecRiskMetrics.high, nonInfoSecRiskMetrics.medium, nonInfoSecRiskMetrics.low]}
                  colors={['#ef4444', '#f59e0b', '#10b981']}
                  title="Risk Distribution"
                />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Control Status</p>
                  <HorizontalBarChart data={nonInfoSecRiskMetrics.byStatus} />
                </div>
              </div>
              
              {/* By category */}
              {nonInfoSecRiskMetrics.byCategory.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">By Risk Category</p>
                  <HorizontalBarChart data={nonInfoSecRiskMetrics.byCategory} />
                </div>
              )}
            </motion.div>
            
            {/* ========== SOA SECTION ========== */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
            >
              <SectionHeader 
                title="Statement of Applicability" 
                icon={<svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
                color="border-emerald-500"
              />
              
              {/* Stats row */}
              <div className="grid grid-cols-4 gap-3 mb-5">
                <StatCard title="Total Controls" value={soaMetrics.totalControls} color="gray" />
                <StatCard title="In Use" value={soaMetrics.used} color="green" subtitle="Linked to risks" />
                <StatCard title="Not Used" value={soaMetrics.notUsed} color="amber" subtitle="No linked risks" />
                <StatCard title="Justifications" value={soaMetrics.hasJustification} color="blue" />
              </div>
              
              {/* Coverage and implementation */}
              <div className="grid grid-cols-2 gap-6 mb-4">
                {/* Coverage gauge */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Control Coverage</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${soaMetrics.completionPercentage}%` }}
                        transition={{ duration: 0.5 }}
                        className="h-full bg-emerald-500 rounded-full"
                      />
                    </div>
                    <span className="text-sm font-bold text-emerald-600">{soaMetrics.completionPercentage}%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{soaMetrics.used} of {soaMetrics.totalControls} controls linked to risks</p>
                </div>
                
                {/* Implementation status */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Risk Control Status</p>
                  <div className="flex gap-3">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-xs text-gray-600">{soaMetrics.implemented} Implemented</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span className="text-xs text-gray-600">{soaMetrics.inProgress} In Progress</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                      <span className="text-xs text-gray-600">{soaMetrics.notStarted} Other</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* By theme */}
              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 mb-3 uppercase">Controls by Theme</p>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(soaMetrics.byTheme).map(([theme, data]) => (
                    <div key={theme} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">{theme}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden flex">
                          <div 
                            className="h-full bg-green-500" 
                            style={{ width: data.total > 0 ? `${(data.used / data.total) * 100}%` : '0%' }}
                          />
                          <div 
                            className="h-full bg-amber-500" 
                            style={{ width: data.total > 0 ? `${(data.notUsed / data.total) * 100}%` : '0%' }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{data.used}/{data.total}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* SoA review date alert */}
              {(() => {
                const nextReview = soaSnap.companyInfo?.nextReviewDate;
                if (!nextReview) {
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-gray-400">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                        <span className="text-sm">No SoA review date set</span>
                      </div>
                    </div>
                  );
                }
                const reviewDate = new Date(nextReview);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const diffDays = Math.ceil((reviewDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                const formattedDate = formatDate(reviewDate);
                if (diffDays < 0) {
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-red-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                        <span className="text-sm font-medium">SoA review is overdue (was due {formattedDate})</span>
                      </div>
                    </div>
                  );
                }
                if (diffDays <= 30) {
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-amber-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
                        <span className="text-sm font-medium">SoA review due in {diffDays} day{diffDays !== 1 ? 's' : ''} ({formattedDate})</span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </motion.div>
            
          </div>
        </div>
      </div>
    </div>
  );
}
